import React, { useEffect, useState, useContext } from "react";
import {
  Grid,
  Typography,
  Button,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { Clock, Globe, Building2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTimezone from "../../hooks/useTimezone";
import OnlyForSuperUser from "../OnlyForSuperUser";
import HelpTooltipIcon from "../HelpTooltipIcon";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },

    sectionCard: {
      borderRadius: 12,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      padding: 16,
      marginBottom: 12,
      transition: "all 0.2s ease",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 4,
    },
    sectionIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: 8,
      background: isDark
        ? "rgba(255,255,255,0.05)"
        : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
    },
    sectionDescription: {
      fontSize: 12,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginBottom: 12,
      lineHeight: 1.5,
    },

    timezoneField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiInputLabel-root": {
        color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.primary.main,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.18)"
          : "rgba(0,0,0,0.18)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",
      },
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      width: "100%",
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#111b21",
      lineHeight: 1.2,
    },
    autocompletePaper: {
      borderRadius: "14px !important",
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: "none",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none",
      marginTop: 4,
      ...(theme.scrollbarStyles || {}),
    },
    autocompleteListbox: {
      maxHeight: 320,
      padding: 6,
      ...(theme.scrollbarStyles || {}),
    },
    autocompleteOption: {
      borderRadius: 8,
      padding: "9px 12px",
      marginBottom: 4,
      fontSize: 14,
      fontWeight: 500,
      color: isDark ? "#ffffff" : "#1a1a1a",
      '&[data-focus="true"]': {
        backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "rgba(6, 207, 156, 0.10)",
      },
      '&[aria-selected="true"][data-focus="true"]': {
        backgroundColor: "rgba(6, 207, 156, 0.15)",
      },
      "&:last-child": {
        marginBottom: 0,
      },
    },

    previewCard: {
      height: "100%",
      boxSizing: "border-box",
      padding: 14,
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      background: isDark
        ? "rgba(255,255,255,0.025)"
        : "rgba(0,0,0,0.02)",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    previewIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 8,
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#ffffff",
      flexShrink: 0,
    },
    previewBody: {
      flex: 1,
      minWidth: 0,
    },
    previewLabel: {
      fontSize: 11,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    previewValue: {
      fontSize: 18,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
      marginTop: 2,
    },
    previewMeta: {
      fontSize: 11,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginTop: 2,
    },
    statusChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      flexShrink: 0,
    },
    statusInherited: {
      background: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.10)",
      color: isDark ? "#fbbf24" : "#92400e",
    },
    statusCustom: {
      background: "rgba(6, 207, 156, 0.12)",
      color: isDark ? "#34d399" : "#0d6e54",
    },

    buttonRow: {
      display: "flex",
      gap: 8,
      marginTop: 12,
      flexWrap: "wrap",
    },
    saveButton: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 18px",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#ffffff",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
      "&.Mui-disabled": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.10)",
        color: isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.35)",
      },
    },
    resetButton: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 18px",
      background: "transparent",
      color: isDark ? "#e9edef" : "#374151",
      border: isDark
        ? "1px solid rgba(255,255,255,0.18)"
        : "1px solid rgba(0,0,0,0.18)",
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
        borderColor: isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",
      },
      "&.Mui-disabled": {
        opacity: 0.4,
      },
    },

    loadingWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
    },
  };
});

const TimezoneTab = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const {
    loading,
    getAvailableTimezones,
    getCompanyTimezone,
    updateDefaultTimezone,
    updateCompanyTimezone,
    previewTimeInTimezone,
  } = useTimezone();

  const [availableTimezones, setAvailableTimezones] = useState([]);
  const [defaultTimezone, setDefaultTimezone] = useState("");
  const [currentCompanyTimezone, setCurrentCompanyTimezone] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [selectedDefaultTimezone, setSelectedDefaultTimezone] = useState("");
  const [previewTime, setPreviewTime] = useState("");
  const [previewDefaultTime, setPreviewDefaultTime] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const timezoneData = await getAvailableTimezones();
        setAvailableTimezones(timezoneData.availableTimezones || []);
        setDefaultTimezone(timezoneData.defaultTimezone || "America/Sao_Paulo");
        setSelectedDefaultTimezone(timezoneData.defaultTimezone || "America/Sao_Paulo");

        const companyTzData = await getCompanyTimezone();
        setCurrentCompanyTimezone(companyTzData);
        setSelectedTimezone(companyTzData.timezone);

        if (companyTzData.timezone) {
          setPreviewTime(previewTimeInTimezone(companyTzData.timezone));
        }
        if (timezoneData.defaultTimezone) {
          setPreviewDefaultTime(previewTimeInTimezone(timezoneData.defaultTimezone));
        }
      } catch (error) {
        console.error("Error loading timezone data:", error);
      } finally {
        setInitialLoad(false);
      }
    };
    loadData();
  }, [getAvailableTimezones, getCompanyTimezone, previewTimeInTimezone]);

  useEffect(() => {
    if (selectedTimezone) {
      const timer = setInterval(() => {
        setPreviewTime(previewTimeInTimezone(selectedTimezone));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedTimezone, previewTimeInTimezone]);

  useEffect(() => {
    if (selectedDefaultTimezone) {
      const timer = setInterval(() => {
        setPreviewDefaultTime(previewTimeInTimezone(selectedDefaultTimezone));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedDefaultTimezone, previewTimeInTimezone]);

  const handleCompanyTimezoneChange = (event, newTimezone) => {
    if (!newTimezone) return;
    setSelectedTimezone(newTimezone);
    setPreviewTime(previewTimeInTimezone(newTimezone));
  };

  const handleDefaultTimezoneChange = (event, newTimezone) => {
    if (!newTimezone) return;
    setSelectedDefaultTimezone(newTimezone);
    setPreviewDefaultTime(previewTimeInTimezone(newTimezone));
  };

  const handleSaveCompanyTimezone = async () => {
    try {
      await updateCompanyTimezone(selectedTimezone);
      const companyTzData = await getCompanyTimezone();
      setCurrentCompanyTimezone(companyTzData);
    } catch (error) {
      console.error("Error saving company timezone:", error);
    }
  };

  const handleSaveDefaultTimezone = async () => {
    try {
      await updateDefaultTimezone(selectedDefaultTimezone);
      setDefaultTimezone(selectedDefaultTimezone);
    } catch (error) {
      console.error("Error saving default timezone:", error);
    }
  };

  const handleResetCompanyTimezone = () => {
    setSelectedTimezone(defaultTimezone);
    setPreviewTime(previewTimeInTimezone(defaultTimezone));
  };

  const FieldGroup = ({ label, helpKey, children }) => (
    <div className={classes.fieldGroup}>
      <span className={classes.fieldLabel}>
        {label}
        <HelpTooltipIcon helpKey={helpKey} size={13} placement="top" />
      </span>
      {children}
    </div>
  );

  if (initialLoad) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingWrap}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Company Timezone Section */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <Building2 size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            {i18n.t("settings.timezone.companyTimezone.title")}
          </Typography>
          <HelpTooltipIcon helpKey="settings.timezone.helps.companyTimezone" />
        </div>
        <Typography className={classes.sectionDescription}>
          {currentCompanyTimezone?.isCustom
            ? i18n.t("settings.timezone.companyTimezone.customHelperText")
            : i18n.t("settings.timezone.companyTimezone.inheritedHelperText")}
        </Typography>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <FieldGroup
              label={i18n.t("settings.timezone.companyTimezone.selectLabel")}
              helpKey="settings.timezone.helps.companyTimezoneField"
            >
            <Autocomplete
              options={availableTimezones}
              value={selectedTimezone || null}
              onChange={handleCompanyTimezoneChange}
              disabled={loading}
              disableClearable
              autoHighlight
              getOptionLabel={(option) => option || ""}
              getOptionSelected={(option, value) => option === value}
              classes={{
                paper: classes.autocompletePaper,
                listbox: classes.autocompleteListbox,
                option: classes.autocompleteOption,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.timezoneField}
                  variant="outlined"
                  size="small"
                  placeholder="Buscar fuso horario"
                />
              )}
            />
            </FieldGroup>

            <div className={classes.buttonRow}>
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={handleSaveCompanyTimezone}
                disabled={loading || selectedTimezone === currentCompanyTimezone?.timezone}
              >
                {loading ? (
                  <CircularProgress size={20} style={{ color: "inherit" }} />
                ) : (
                  i18n.t("settings.timezone.buttons.save")
                )}
              </Button>
              <Button
                variant="outlined"
                className={classes.resetButton}
                onClick={handleResetCompanyTimezone}
                disabled={loading || selectedTimezone === defaultTimezone}
              >
                {i18n.t("settings.timezone.buttons.useDefault")}
              </Button>
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className={classes.previewCard}>
              <span className={classes.previewIconWrap}>
                <Clock size={20} strokeWidth={1.8} />
              </span>
              <div className={classes.previewBody}>
                <div className={classes.previewLabel}>
                  {i18n.t("settings.timezone.preview.currentTime")}
                </div>
                <div className={classes.previewValue}>{previewTime}</div>
                <div className={classes.previewMeta}>{selectedTimezone}</div>
              </div>
              <span
                className={`${classes.statusChip} ${
                  currentCompanyTimezone?.isCustom
                    ? classes.statusCustom
                    : classes.statusInherited
                }`}
              >
                {currentCompanyTimezone?.isCustom
                  ? i18n.t("settings.timezone.status.custom")
                  : i18n.t("settings.timezone.status.inherited")}
              </span>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* Super Admin Section */}
      <OnlyForSuperUser
        user={user}
        yes={() => (
          <div className={classes.sectionCard}>
            <div className={classes.sectionHeader}>
              <span className={classes.sectionIconWrap}>
                <Globe size={18} strokeWidth={1.8} />
              </span>
              <Typography className={classes.sectionTitle}>
                {i18n.t("settings.timezone.defaultTimezone.title")}
              </Typography>
              <HelpTooltipIcon helpKey="settings.timezone.helps.defaultTimezone" />
            </div>
            <Typography className={classes.sectionDescription}>
              {i18n.t("settings.timezone.defaultTimezone.helperText")}
            </Typography>

            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <FieldGroup
                  label={i18n.t("settings.timezone.defaultTimezone.selectLabel")}
                  helpKey="settings.timezone.helps.defaultTimezoneField"
                >
                <Autocomplete
                  options={availableTimezones}
                  value={selectedDefaultTimezone || null}
                  onChange={handleDefaultTimezoneChange}
                  disabled={loading}
                  disableClearable
                  autoHighlight
                  getOptionLabel={(option) => option || ""}
                  getOptionSelected={(option, value) => option === value}
                  classes={{
                    paper: classes.autocompletePaper,
                    listbox: classes.autocompleteListbox,
                    option: classes.autocompleteOption,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={classes.timezoneField}
                      variant="outlined"
                      size="small"
                      placeholder="Buscar fuso horario"
                    />
                  )}
                />
                </FieldGroup>

                <div className={classes.buttonRow}>
                  <Button
                    variant="contained"
                    className={classes.saveButton}
                    onClick={handleSaveDefaultTimezone}
                    disabled={loading || selectedDefaultTimezone === defaultTimezone}
                  >
                    {loading ? (
                      <CircularProgress size={20} style={{ color: "inherit" }} />
                    ) : (
                      i18n.t("settings.timezone.buttons.saveDefault")
                    )}
                  </Button>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.previewCard}>
                  <span className={classes.previewIconWrap}>
                    <Globe size={20} strokeWidth={1.8} />
                  </span>
                  <div className={classes.previewBody}>
                    <div className={classes.previewLabel}>
                      {i18n.t("settings.timezone.preview.defaultTime")}
                    </div>
                    <div className={classes.previewValue}>{previewDefaultTime}</div>
                    <div className={classes.previewMeta}>{selectedDefaultTimezone}</div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      />
    </div>
  );
};

export default TimezoneTab;
